import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, Input, Output, Inject, LOCALE_ID, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { Club, User, AuthenticatedUser } from '~core/models';
import { DefaultNotificationService, NavigationService, UserService, StorageService, UserManagementService } from '~core/services';
import { isEmailValid, isPasswordValid, noWhitespaceCharacters } from '~shared/directives/form-validators';
import { CustomerStatusTranslationsService, SharedTranslationsService } from '~shared/translations';
import { NotificationType } from '~core/enum';


/**
 * Component responsible for club-overview user administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-club-user-create',
  templateUrl: './club-user-create.component.html',
})
export class ClubUserCreateComponent implements OnInit, OnDestroy {
  public anim: any = '';
  public spinnerName: string = 'createUserSpinner';
  public modalOpened: boolean = false;
  public userForm: UntypedFormGroup;
  public passwordType: string = 'password';
  @Input() public listOfClubs: Array<Club>;
  @Input() public user: User;
  @Output() public userChange: EventEmitter<User> = new EventEmitter();
  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;

  constructor(
    private notificationService: DefaultNotificationService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private customerStatusTranslations: CustomerStatusTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public alertTranslations: SharedTranslationsService,
    public navigationService: NavigationService,
    public userService: UserService,
    public userModalService: UserManagementService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
    @Inject(LOCALE_ID) public locale: string,
    public storage: StorageService
  ) {
    this.storage.authenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
  }

  public ngOnDestroy(): void { }

  /**
   * On init create the club-overview form, get countries and all clubs.
   */
  public ngOnInit(): any {
    this.createForm();
  }

  /**
   * Creates user form.
   */
  private createForm(): void {
    this.userForm = this.formBuilder.group({
      firstName: [null, [
        Validators.required,
        Validators.maxLength(100),
        noWhitespaceCharacters
      ]],
      lastName: [null, [
        Validators.required,
        Validators.maxLength(100),
        noWhitespaceCharacters
      ]],
      username: [null, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        noWhitespaceCharacters
      ]],
      password: [null, [
        Validators.required,
        isPasswordValid
      ]],
      type: [null, [
        Validators.required
      ]],
      email: [null, [
        Validators.required,
        Validators.maxLength(255),
        noWhitespaceCharacters,
        isEmailValid
      ]],
      tokenLifetime: [3600],
      clubs: [this.selectedClub['@id']],
      forcePasswordChange: [true]
    });
  }

  /**
   * On form submit create a new user.
   */
  public submitUser(): any {
    if (this.userForm.valid) {
      this.spinnerService.show(this.spinnerName);

      if (this.userForm.get('type').value === 'Admin') {
        this.userForm.removeControl('clubs');
      } else {
        let club = this.userForm.get('clubs').value;
        if (Array.isArray(club)) {
          club = club[0];
        }
        this.userForm.get('clubs').setValue([club]);
      }

      if (this.userForm.get('tokenLifetime').value === '28800') {
        const eightHours: number = 28800;
        this.userForm.get('tokenLifetime').setValue(eightHours);
      } else {
        const oneHour: number = 3600;
        this.userForm.get('tokenLifetime').setValue(oneHour);
      }

      this.userService.create(this.userForm.value).pipe(untilDestroyed(this))
        .subscribe({
          next: (res: User) => {
            this.user = new User(res, this.customerStatusTranslations);
            this.spinnerService.hide(this.spinnerName);
            this.userForm.reset();
            this.userModalService.toggleModal(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.alertTranslations.i18nUserCreated
            });
            this.router.navigate(['/manage/users', this.user.id]);
          },
          error: (error: HttpErrorResponse) => {
            if (error.error !== null) {
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: error.error['hydra:description']
              });
            }
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

  /**
   * Toggles the password.
   */
  public togglePassword(): void {
    if (this.passwordType === 'password') {
      this.passwordType = 'text';
    } else {
      this.passwordType = 'password';
    }
  }

}
